import { useState, useCallback, useRef, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Select,
  Grid,
  GridItem,
  Box,
  Text,
  Center,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import { GoogleMap, useJsApiLoader, Marker } from '@react-google-maps/api';
import { apiPostalCodeLookup } from 'api/quote-marketplace';
import { isValidPostalCode } from '../utils/postalCode';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

interface ViewGoogleMapsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (
    streetNumber: string,
    streetName: string,
    latitude: number,
    longitude: number,
    postalCode: string,
    city: string,
  ) => void;
  postalCode: string;
  city: string;
  initialStreetNumber?: string;
  initialStreetName?: string;
  initialLatitude?: number | null;
  initialLongitude?: number | null;
}

const mapContainerStyle = {
  width: '100%',
  height: '100%',
};

const defaultCenter = {
  lat: 43.6532,
  lng: -79.3832,
};

const ViewGoogleMapsModal = ({
  isOpen,
  onClose,
  onSelect,
  postalCode: initialPostalCode,
  city: initialCity,
  initialStreetNumber = '',
  initialStreetName = '',
  initialLatitude = null,
  initialLongitude = null,
}: ViewGoogleMapsModalProps) => {
  const hasInitialCoords =
    initialLatitude !== null && initialLongitude !== null;

  const [streetNumber, setStreetNumber] = useState(initialStreetNumber);
  const [streetName, setStreetName] = useState(initialStreetName);
  const [localPostalCode, setLocalPostalCode] = useState(initialPostalCode);
  const [localCity, setLocalCity] = useState(initialCity);
  const [availableCities, setAvailableCities] = useState<string[]>([]);
  const [postalCodeError, setPostalCodeError] = useState<string | null>(null);
  const [lastLookedUpPostalCode, setLastLookedUpPostalCode] =
    useState(initialPostalCode);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mapLoaded, setMapLoaded] = useState(hasInitialCoords);
  const [center, setCenter] = useState(
    hasInitialCoords
      ? { lat: initialLatitude, lng: initialLongitude }
      : defaultCenter,
  );
  const [markerPosition, setMarkerPosition] =
    useState<google.maps.LatLngLiteral | null>(
      hasInitialCoords ? { lat: initialLatitude, lng: initialLongitude } : null,
    );

  const mapRef = useRef<google.maps.Map | null>(null);
  const geocoderRef = useRef<google.maps.Geocoder | null>(null);

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
  });

  useEffect(() => {
    if (isLoaded && !geocoderRef.current) {
      geocoderRef.current = new google.maps.Geocoder();
    }
  }, [isLoaded]);

  useEffect(() => {
    if (isOpen) {
      setStreetNumber(initialStreetNumber);
      setStreetName(initialStreetName);
      setLocalPostalCode(initialPostalCode);
      setLocalCity(initialCity);
      setAvailableCities([]);
      setPostalCodeError(null);
      setLastLookedUpPostalCode(initialPostalCode);
      if (hasInitialCoords) {
        const coords = { lat: initialLatitude, lng: initialLongitude };
        setCenter(coords);
        setMarkerPosition(coords);
        setMapLoaded(true);
      } else {
        setMapLoaded(false);
        setMarkerPosition(null);
        setCenter(defaultCenter);
      }
      setError(null);
    }
  }, [
    isOpen,
    initialStreetNumber,
    initialStreetName,
    initialLatitude,
    initialLongitude,
    initialPostalCode,
    initialCity,
    hasInitialCoords,
  ]);

  const handlePostalCodeChange = (value: string) => {
    setLocalPostalCode(value.toUpperCase());
    setPostalCodeError(null);
  };

  const onMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
  }, []);

  const handleLookup = async () => {
    if (!streetNumber || !streetName) {
      setError('Please enter street number and street name');
      return;
    }

    const trimmedPostalCode = localPostalCode.trim().toUpperCase();

    if (!isValidPostalCode(trimmedPostalCode)) {
      setPostalCodeError('Invalid postal code format (e.g., A1A 1A1)');
      return;
    }

    if (!geocoderRef.current) {
      setError('Map not ready. Please try again.');
      return;
    }

    setIsLoading(true);
    setError(null);
    setPostalCodeError(null);

    // If postal code changed, lookup city first and reset street fields
    let cityToUse = localCity;
    if (trimmedPostalCode !== lastLookedUpPostalCode) {
      try {
        const result = await apiPostalCodeLookup(trimmedPostalCode);
        if (result.found && result.cities && result.cities.length > 0) {
          cityToUse = result.cities[0];
          setLocalCity(cityToUse);
          if (result.cities.length > 1) {
            setAvailableCities(result.cities);
          } else {
            setAvailableCities([]);
          }
          setLastLookedUpPostalCode(trimmedPostalCode);

          // Reset street fields and map when postal code changes
          setStreetNumber('');
          setStreetName('');
          setMapLoaded(false);
          setMarkerPosition(null);
          setIsLoading(false);
          return; // User needs to re-enter street info for new postal code
        } else {
          setIsLoading(false);
          setPostalCodeError('Could not find city for this postal code');
          return;
        }
      } catch {
        setIsLoading(false);
        setPostalCodeError('Error looking up postal code');
        return;
      }
    }

    if (!cityToUse) {
      setIsLoading(false);
      setError('Could not determine city for this postal code');
      return;
    }

    const address = `${streetNumber} ${streetName}, ${cityToUse}, ${trimmedPostalCode}, Canada`;

    geocoderRef.current.geocode({ address }, (results, status) => {
      setIsLoading(false);

      if (status === 'OK' && results && results.length > 0) {
        const location = results[0].geometry.location;
        const newCenter = {
          lat: location.lat(),
          lng: location.lng(),
        };
        setCenter(newCenter);
        setMarkerPosition(newCenter);
        setMapLoaded(true);

        if (mapRef.current) {
          mapRef.current.setZoom(18);
          mapRef.current.panTo(newCenter);
        }
      } else {
        setError('Address not found. Please check the address and try again.');
      }
    });
  };

  const handleSelect = () => {
    if (markerPosition) {
      onSelect(
        streetNumber,
        streetName,
        markerPosition.lat,
        markerPosition.lng,
        localPostalCode,
        localCity,
      );
    }
    onClose();
  };

  const handleClose = () => {
    onClose();
  };

  if (loadError) {
    return (
      <Modal isOpen={isOpen} onClose={handleClose} size="xl" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Error</ModalHeader>
          <ModalBody>
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              Failed to load Google Maps
            </Alert>
          </ModalBody>
          <ModalFooter>
            <Button onClick={handleClose}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="xl" isCentered>
      <ModalOverlay />
      <ModalContent maxW="700px">
        <ModalHeader>View Map - Garaging Location</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Grid templateColumns="repeat(2, 1fr)" gap={4} mb={4}>
            <GridItem>
              <FormControl>
                <FormLabel fontSize="sm">Street Number</FormLabel>
                <Input
                  variant="main"
                  size="sm"
                  value={streetNumber}
                  onChange={(e) => setStreetNumber(e.target.value)}
                  placeholder="123"
                />
              </FormControl>
            </GridItem>
            <GridItem>
              <FormControl>
                <FormLabel fontSize="sm">Street Name</FormLabel>
                <Input
                  variant="main"
                  size="sm"
                  value={streetName}
                  onChange={(e) => setStreetName(e.target.value)}
                  placeholder="Main Street"
                />
              </FormControl>
            </GridItem>
            <GridItem>
              <FormControl isInvalid={!!postalCodeError}>
                <FormLabel fontSize="sm">Postal Code</FormLabel>
                <Input
                  variant="main"
                  size="sm"
                  value={localPostalCode}
                  onChange={(e) => handlePostalCodeChange(e.target.value)}
                  placeholder="A1A 1A1"
                  maxLength={7}
                />
                <FormErrorMessage>{postalCodeError}</FormErrorMessage>
              </FormControl>
            </GridItem>
            <GridItem>
              <FormControl>
                <FormLabel fontSize="sm">City</FormLabel>
                {availableCities.length > 1 ? (
                  <Select
                    variant="main"
                    size="sm"
                    value={localCity}
                    onChange={(e) => setLocalCity(e.target.value)}
                  >
                    {availableCities.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </Select>
                ) : (
                  <Input
                    variant="main"
                    size="sm"
                    value={localCity}
                    isReadOnly
                    bg="gray.100"
                  />
                )}
              </FormControl>
            </GridItem>
          </Grid>

          <Button
            colorScheme="brand"
            size="sm"
            onClick={handleLookup}
            isLoading={isLoading}
            isDisabled={!isLoaded}
            mb={4}
          >
            Look Up Address
          </Button>

          {error && (
            <Alert status="error" mb={4} borderRadius="md">
              <AlertIcon />
              {error}
            </Alert>
          )}

          <Box
            h="300px"
            borderRadius="md"
            overflow="hidden"
            borderWidth="1px"
            borderColor="gray.200"
          >
            {!isLoaded ? (
              <Center h="100%" bg="gray.50">
                <Text color="gray.500" fontSize="sm">
                  Loading map...
                </Text>
              </Center>
            ) : !mapLoaded ? (
              <Center h="100%" bg="gray.50">
                <Text color="gray.500" fontSize="sm">
                  Enter address and click "Look Up Address" to view map
                </Text>
              </Center>
            ) : (
              <GoogleMap
                mapContainerStyle={mapContainerStyle}
                center={center}
                zoom={18}
                onLoad={onMapLoad}
                mapTypeId="hybrid"
              >
                {markerPosition && <Marker position={markerPosition} />}
              </GoogleMap>
            )}
          </Box>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={handleClose}>
            Cancel
          </Button>
          <Button
            colorScheme="brand"
            onClick={handleSelect}
            isDisabled={!streetNumber || !streetName || !markerPosition}
          >
            Select
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ViewGoogleMapsModal;
